import React, { useEffect, useRef, useState } from "react";
import { EMPTY_NOTES_MESSAGE } from "../../../../../constants/Dictionary";
import { StyledScrollableContainer, StyledBodyNotes } from "./Note.styled";
import { NoteItem } from "./NoteItem/NoteItem";
import { Loader } from "../../../../../components/core/Loader/Loader";
import { CollaboratorsCmSheetNote } from "../../../../../types/api/collaborators.type";
import { OperationEnum } from "../../../../../types/api/generic.type";
import { EmptyState } from "../../../../../components/EmptyState/EmptyState";
import notesImage from "../../../../../images/notes.png";
import { CmSheetSectionProps } from "../SchedaCM";
import { NoteFooter } from "./NoteFooter/NoteFooter";
import {
  useCmSheetNotes,
  useUpdateCmSheetNotes,
} from "../../../../../hooks/cmSheet/note.hook";

export const Note = ({ footer, cmSheetInfo, setEdit }: CmSheetSectionProps) => {
  const { data, isFetching } = useCmSheetNotes(cmSheetInfo.id);
  const { updateCmSheetNotes, isUpdating } = useUpdateCmSheetNotes(
    cmSheetInfo.id,
  );
  const [isNew, setIsNew] = useState<boolean>(false);
  const [hasData, setHasData] = useState<boolean | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  const onSave = async (text: string) => {
    try {
      await updateCmSheetNotes([
        {
          operation: OperationEnum.ADD,
          entityId: null,
          data: { text: text },
        },
      ]);
    } finally {
      setHasData(null);
    }
  };

  async function onDelete(id: number) {
    try {
      await updateCmSheetNotes([
        {
          operation: OperationEnum.DELETE,
          entityId: id,
          data: null,
        },
      ]);
    } finally {
      setHasData(null);
    }
  }

  useEffect(() => {
    footer(<NoteFooter setEdit={setEdit} onSave={onSave} />);
  }, [cmSheetInfo, isUpdating]);

  const showLoader = isFetching && hasData === null;
  const showEmptyState = !isFetching && !hasData;

  useEffect(() => {
    setHasData(null);
  }, [cmSheetInfo]);

  useEffect(() => {
    if (!isFetching) {
      if (data && hasData === null) {
        const newHasData = data.length > 0;
        setIsNew(true);
        setHasData(newHasData);
      }
    }
  }, [data, hasData, isFetching]);

  useEffect(() => {
    const div = containerRef.current;
    if (div && isNew && containerRef) {
      div.scrollTop = div.scrollHeight;
      setIsNew(false);
    }
  }, [data, isNew, containerRef]);

  return (
    <>
      {showLoader && <Loader loading={true} className="cm-sheet-loader" />}
      {showEmptyState ? (
        <div className="cm-sheet-empty-state">
          <EmptyState label={EMPTY_NOTES_MESSAGE} imageSrc={notesImage} />
        </div>
      ) : (
        <Loader loading={isUpdating}>
          <StyledBodyNotes>
            <StyledScrollableContainer ref={containerRef}>
              {data?.map((item: CollaboratorsCmSheetNote) => (
                <NoteItem
                  key={item.id}
                  id={item.id}
                  name={item.collaboratorName}
                  surname={item.collaboratorSurname}
                  role={item.collaboratorRole}
                  date={item.createdAt}
                  onDelete={onDelete}
                  note={item.text}
                  collaboratorId={item.collaboratorId}
                />
              ))}
            </StyledScrollableContainer>
          </StyledBodyNotes>
        </Loader>
      )}
    </>
  );
};
